import { API_GROUPS } from "../api/groups";
import { c } from "./colors";

/**
 * Help-index entries for the hand-written command groups. Descriptions here
 * are written for the root help listing and intentionally differ from the
 * commands' own `description` (shown in per-command help).
 */
const GET_STARTED_SECTION: HelpSection = {
	label: "Get Started",
	commands: [
		{
			name: "quickstart",
			description: "Start here — choose or create the business used by the CLI",
		},
		{
			name: "apps",
			description: "Build and deploy fully-hosted web apps (*.whop.app)",
		},
		{ name: "upgrade", description: "Update the CLI to the latest version" },
	],
};

const AUTH_SECTION: HelpSection = {
	label: "Auth",
	commands: [
		{ name: "auth", description: "Manage authentication" },
		{ name: "login", description: "Sign in to Whop" },
		{ name: "logout", description: "Sign out" },
	],
};

const INTEGRATIONS = [
	{ name: "completions", desc: "Generate shell completion script" },
	{ name: "mcp add", desc: "Register as MCP server" },
	{ name: "skills", desc: "Sync skill files to agents (add, list)" },
];

const GLOBAL_FLAGS = [
	{
		flag: "--filter-output <keys>",
		desc: "Filter output by key paths (e.g. foo,bar.baz,a[0,3])",
	},
	{ flag: "--format <toon|json|yaml|md|jsonl>", desc: "Output format" },
	{ flag: "--full-output", desc: "Show full output envelope" },
	{ flag: "--help", desc: "Show help" },
	{ flag: "--llms, --llms-full", desc: "Print LLM-readable manifest" },
	{ flag: "--mcp", desc: "Start as MCP stdio server" },
	{ flag: "--schema", desc: "Show JSON Schema for command" },
	{
		flag: "--token-count",
		desc: "Print token count of output (instead of output)",
	},
	{ flag: "--token-limit <n>", desc: "Limit output to n tokens" },
	{ flag: "--token-offset <n>", desc: "Skip first n tokens of output" },
	{ flag: "--version", desc: "Show version" },
];

type HelpCommand = {
	name: string;
	description: string;
};

type HelpSection = {
	label: string;
	commands: HelpCommand[];
};

export function buildGroupedHelp(version: string, description: string): string {
	const lines: string[] = [];

	// groups.json is ordered by the spec's x-tagGroups taxonomy, so grouping
	// by each command's `group` label preserves both section and command order.
	const apiSections: HelpSection[] = [];
	for (const { name, description, group } of API_GROUPS) {
		let section = apiSections.find((s) => s.label === group);
		if (!section) apiSections.push((section = { label: group, commands: [] }));
		section.commands.push({ name, description });
	}

	const commerceSections = apiSections.filter(
		(section) => section.label.toLowerCase() === "commerce",
	);
	const otherApiSections = apiSections.filter(
		(section) => section.label.toLowerCase() !== "commerce",
	);
	const allSections = [
		GET_STARTED_SECTION,
		...commerceSections,
		...otherApiSections,
		AUTH_SECTION,
	].filter((section) => section.commands.length > 0);
	const maxCmdLen = Math.max(
		...allSections.flatMap((section) =>
			section.commands.map((command) => command.name.length),
		),
	);

	lines.push(`whop@${version} — ${description}`);
	lines.push("");
	lines.push("Usage: whop <command>");

	for (const section of allSections) {
		lines.push("");
		lines.push(c.heading(section.label.toUpperCase()));
		for (const cmd of section.commands) {
			const pad = " ".repeat(maxCmdLen - cmd.name.length);
			lines.push(
				`  ${c.command(cmd.name)}${pad}  ${c.description(cmd.description)}`,
			);
		}
	}

	const maxIntLen = Math.max(...INTEGRATIONS.map((i) => i.name.length));
	lines.push("");
	lines.push("Integrations:");
	for (const i of INTEGRATIONS) {
		lines.push(
			`  ${c.command(i.name)}${" ".repeat(maxIntLen - i.name.length)}  ${c.description(i.desc)}`,
		);
	}

	const maxFlagLen = Math.max(...GLOBAL_FLAGS.map((f) => f.flag.length));
	lines.push("");
	lines.push("Global Options:");
	for (const f of GLOBAL_FLAGS) {
		lines.push(
			`  ${c.flag(f.flag)}${" ".repeat(maxFlagLen - f.flag.length)}  ${c.description(f.desc)}`,
		);
	}

	return lines.join("\n");
}
